import { useState, useEffect } from "react";
import { AppState } from "react-native";
import { postEntry, getEntries, getHoursByRange, postEntryRequest} from "../../services/TimeTracking/api";
import { saveClockState, loadClockState, clearClockState } from "../../utils/storage";
import { registerBackgroundTask } from "../../utils/backgroundTask";
import { useAuth } from "../../context/Auth/AuthContext";

export default function useTimeTracking() {
  const [clockInTime, setClockInTime] = useState<Date | null>(null);
  const [clockOutTime, setClockOutTime] = useState<Date | null>(null);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [lunchStartTime, setLunchStartTime] = useState<Date | null>(null);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isOnLunch, setIsOnLunch] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [todaysLogs, setTodaysLogs] = useState<{ time: string; type: string }[]>([]);
  const { isAuthenticated, userInfo, isAuthLoading } = useAuth();
  const appUserId = userInfo?.appUserId;
  const [activeTimeSeconds, setActiveTimeSeconds] = useState(0);
  const [breakTimeSeconds, setBreakTimeSeconds] = useState(0);
  const [weeklyTimeSeconds, setWeeklyTimeSeconds] = useState(0);
  const [weeklyDailyHours, setWeeklyDailyHours] = useState<number[]>([]);
  const [errorNotification, setErrorNotification] = useState<string | null>(null);
  const [activeElapsed, setActiveElapsed] = useState(0);
  const [breakElapsed, setBreakElapsed] = useState(0);

  const defaultTasks = "{\"task1\": \"description1\", \"task2\": \"description2\"}";

  useEffect(() => {
    registerBackgroundTask();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const active = clockInTime && !clockOutTime;
  
    if (active) {
      interval = setInterval(() => {
        const { activeTime, breakTime } = calculateActiveAndBreakTime(todaysLogs);
        setActiveElapsed(activeTime);
        setBreakElapsed(breakTime);
      }, 1000);
    }
  
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [clockInTime, clockOutTime, isOnBreak, isOnLunch, todaysLogs]);

  const handleError = (message: string, error: any) => {
    console.error(message, error);
    setErrorNotification(message);
  };

  const calculateElapsedTime = (logEntries: { time: string; type: string }[]): number => {
    const sorted = [...logEntries].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  
    let totalWorked = 0;
    const breaks: [number, number][] = [];
  
    let clockInTime: number | null = null;
    let currentBreakStart: number | null = null;
  
    for (const entry of sorted) {
      const timestamp = new Date(entry.time).getTime() / 1000;
  
      switch (entry.type) {
        case "ClockIn":
          if (clockInTime === null) clockInTime = timestamp;
          break;
  
        case "ClockOut":
          if (clockInTime !== null) {
            totalWorked += timestamp - clockInTime;
            clockInTime = null;
          }
          break;
  
        case "BreakOut":
        case "LunchOut":
          if (currentBreakStart === null) currentBreakStart = timestamp;
          break;
  
        case "BreakIn":
        case "LunchIn":
          if (currentBreakStart !== null) {
            breaks.push([currentBreakStart, timestamp]);
            currentBreakStart = null;
          }
          break;
      }
    }
  
    // ClockIn sin ClockOut
    if (clockInTime !== null) {
      totalWorked += Date.now() / 1000 - clockInTime;
    }
  
    // Break iniciado pero no terminado
    if (currentBreakStart !== null) {
      breaks.push([currentBreakStart, Date.now() / 1000]);
    }
  
    const totalBreakTime = breaks.reduce((sum, [start, end]) => sum + (end - start), 0);
  
    return Math.floor(totalWorked - totalBreakTime);
  }

  function calculateActiveAndBreakTime(logEntries: { time: string; type: string }[]) {
    const sorted = [...logEntries].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  
    let activeTime = 0;
    let breakTime = 0;
    let currentClockIn: number | null = null;
    let currentBreak: number | null = null;
  
    for (const entry of sorted) {
      const time = new Date(entry.time).getTime() / 1000;
  
      switch (entry.type) {
        case "ClockIn":
          currentClockIn = time;
          break;
        case "ClockOut":
          if (currentClockIn !== null) {
            activeTime += time - currentClockIn;
            currentClockIn = null;
          }
          break;
        case "BreakOut":
        case "LunchOut":
          currentBreak = time;
          break;
        case "BreakIn":
        case "LunchIn":
          if (currentBreak !== null) {
            breakTime += time - currentBreak;
            currentBreak = null;
          }
          break;
      }
    }
  
    if (currentClockIn) {
      activeTime += Date.now() / 1000 - currentClockIn;
    }
    if (currentBreak) {
      breakTime += Date.now() / 1000 - currentBreak;
    }
  
    return {
      activeTime: Math.floor(activeTime),
      breakTime: Math.floor(breakTime),
    };
  }

  function calculateWeeklyTotal(logEntries: { time: string; type: string }[]) {
    const groupedByDay: Record<string, number> = {};
    let clockIn: number | null = null;
  
    for (const entry of logEntries) {
      const entryDate = new Date(entry.time);
      const dayKey = entryDate.toLocaleDateString(); // agrupa por fecha
  
      const timestamp = entryDate.getTime() / 1000;
  
      switch (entry.type) {
        case "ClockIn":
          clockIn = timestamp;
          break;
        case "ClockOut":
          if (clockIn !== null) {
            const duration = timestamp - clockIn;
            groupedByDay[dayKey] = (groupedByDay[dayKey] || 0) + duration;
            clockIn = null;
          }
          break;
      }
    }

    const totalSeconds = Object.values(groupedByDay).reduce((a, b) => a + b, 0);
    return Math.floor(totalSeconds);
  };

  
  async function getWeeklyHours(appUserId: number): Promise<number[]> {
    try {
      console.log("📤 Fetching weekly hours for AppUser:", appUserId);
  
      const response = await getHoursByRange(appUserId, undefined, undefined, "week");
  
      // Validar que la respuesta tenga el formato esperado
      if (!response || typeof response !== "object" || !response.dailyMinutes) {
        handleError("Data in different format", new Error("API error or missing data"));
        throw new Error("API error or missing dailyMinutes");
      }
  
      const dailyMinutes = response.dailyMinutes;
  
      // Mapa base para los días de la semana
      const weekMap: Record<number, number> = {
        0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0
      };
  
      for (const dateStr in dailyMinutes) {
        const date = new Date(dateStr);
        const day = date.getDay(); // 0 (Sunday) to 6 (Saturday)
        const minutesWorked = dailyMinutes[dateStr]?.minutesWorked ?? 0;
  
        console.log(`📅 ${dateStr} (day ${day}) - ${minutesWorked} minutes`);
        weekMap[day] = Math.floor(minutesWorked / 60); // convertir a horas
      }
  
      // Orden de lunes (1) a domingo (0)
      const orderedDays = [1, 2, 3, 4, 5, 6, 0];
      const weeklyHours = orderedDays.map(day => weekMap[day]);
  
      console.log("✅ Final weekly hours:", weeklyHours);
      return weeklyHours;
    } catch (error: any) {
      console.error("❌ Error fetching weekly hours:", error.message);
      return [0, 0, 0, 0, 0, 0, 0];
    }
  }

  const updateIsWorking = (logs: { time: string; type: string }[] = todaysLogs) => {
    if (!logs.length) {
      setIsWorking(false);
      return;
    }
  
    const sortedLogs = [...logs].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  
    let lastClockInIndex = -1;
    let lastClockOutIndex = -1;
  
    for (let i = 0; i < sortedLogs.length; i++) {
      const entry = sortedLogs[i].type;
      if (entry === "ClockIn") lastClockInIndex = i;
      if (entry === "ClockOut") lastClockOutIndex = i;
    }
  
    const isCurrentlyWorking = lastClockInIndex > lastClockOutIndex;
    setIsWorking(isCurrentlyWorking);
  };

  useEffect(() => {
    async function loadState() {
      if (!appUserId) return;

      const logs = await getEntries(appUserId);
      const today = new Date().toDateString();
      const filtered = logs.filter(entry => new Date(entry.time).toDateString() === today);

      setTodaysLogs(filtered);

      const lastClockIn = [...filtered].reverse().find(entry => entry.type === "ClockIn");
      if (!lastClockIn) {
        await clearClockState();
        setClockInTime(null);
        setClockOutTime(null);
        setBreakStartTime(null);
        setLunchStartTime(null);
        setIsOnBreak(false);
        setIsOnLunch(false);
        setElapsedTime(0);
        updateIsWorking();
        return;
      }

      const lastEntry = [...filtered].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())[0];

      const isCurrentlyOnBreak = lastEntry?.type === "BreakOut";
      const isCurrentlyOnLunch = lastEntry?.type === "LunchOut";

      setIsOnBreak(isCurrentlyOnBreak);
      setIsOnLunch(isCurrentlyOnLunch);
      setBreakStartTime(isCurrentlyOnBreak ? new Date(lastEntry.time) : null);
      setLunchStartTime(isCurrentlyOnLunch ? new Date(lastEntry.time) : null);

      const state = await loadClockState();
      setClockInTime(state?.clockInTime ? new Date(state.clockInTime) : new Date(lastClockIn.time));
      setClockOutTime(state?.clockOutTime ? new Date(state.clockOutTime) : null);

      setElapsedTime(calculateElapsedTime(filtered));
      setActiveElapsed(state?.activeElapsed || 0);
      setBreakElapsed(state?.breakElapsed || 0);
      
      updateIsWorking();
    }

    loadState();
  }, [isAuthLoading]);

  useEffect(() => {
    const listener = AppState.addEventListener("change", async (state) => {
      if (state === "background") {
        await saveClockState({
          clockInTime: clockInTime?.toISOString() || null,
          lastElapsedTime: elapsedTime,
          lastSavedTime: Date.now(),
          activeElapsed,
          breakElapsed,
        });
      }
      if (state === "active") {
        const saved = await loadClockState();
        if (saved?.lastSavedTime) {
          const timePassed = Math.floor((Date.now() - saved.lastSavedTime) / 1000);

          const newElapsedTime = (saved.lastElapsedTime || 0) + timePassed;
          const newActiveElapsed = (saved.activeElapsed || 0) + (isWorking && !isOnBreak && !isOnLunch ? timePassed : 0);
          const newBreakElapsed = (saved.breakElapsed || 0) + ((isOnBreak || isOnLunch) && !clockOutTime ? timePassed : 0);

          setElapsedTime(newElapsedTime);
          setActiveElapsed(newActiveElapsed);
          setBreakElapsed(newBreakElapsed);
        }
      }
    });
    return () => listener.remove();
  }, [clockInTime, elapsedTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const active = clockInTime && !clockOutTime && !isOnBreak && !isOnLunch;

    if (active) {
      interval = setInterval(() => {
        setElapsedTime(calculateElapsedTime(todaysLogs));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [clockInTime, clockOutTime, isOnBreak, isOnLunch, todaysLogs]);

  useEffect(() => {
    updateIsWorking();
  }, [clockInTime, clockOutTime, isOnBreak, isOnLunch]);

  useEffect(() => {
    async function loadWeeklyHours() {
      if (!appUserId) return;
  
      try {
        console.log("🔵 Cargando horas semanales...");
        const weeklyHours = await getWeeklyHours(appUserId);
        console.log("🟢 Horas semanales obtenidas:", weeklyHours);
        setWeeklyDailyHours(weeklyHours);
      } catch (error) {
        console.error("🔴 Error al cargar horas semanales:", error);
        handleError("Failed to load weekly hours. Please try again later.", error);
      
        setWeeklyDailyHours([0, 0, 0, 0, 0, 0, 0]); // por si falla, carga 0
      }
    }
  
    loadWeeklyHours();
  }, [isAuthLoading]);

  const updateLogsAndElapsed = async () => {
    if (!appUserId) return;
    const logs = await getEntries(appUserId);
    const today = new Date().toDateString();
    const filtered = logs.filter(entry => new Date(entry.time).toDateString() === today);
    const { activeTime, breakTime } = calculateActiveAndBreakTime(filtered);
    
    setTodaysLogs(filtered);
    setActiveTimeSeconds(activeTime);
    setBreakTimeSeconds(breakTime);
    setElapsedTime(calculateElapsedTime(filtered));
    setWeeklyTimeSeconds(calculateWeeklyTotal(logs));
    updateIsWorking(filtered); // 🛠️ aquí le paso directamente los nuevos logs
  };

  const getSafeTasks = (tasks?: string) => {
    return typeof tasks === "string" && tasks.trim() !== "" ? tasks : defaultTasks;
  };

  const handleClockIn = async (tasks?: string) => {
    if (!appUserId) return;
    const now = new Date();
    try{
      setClockInTime(now);
      setClockOutTime(null);
      await postEntry(appUserId, "ClockIn", false, getSafeTasks(tasks));
      await saveClockState({ clockInTime: now.toISOString() });
      await updateLogsAndElapsed();
      updateIsWorking();
      await updateLogsAndElapsed();
    }
    catch (err) {
      handleError("An error has occurred!", err);
    }
  };



  const handleClockOut = async (tasks?: string) => {
    if (!appUserId) return;
    const now = new Date();
    try{
      setClockOutTime(now);
      setClockInTime(null);

      await postEntry(appUserId, "ClockOut", false, getSafeTasks(tasks));
      await clearClockState();
      await updateLogsAndElapsed();
      updateIsWorking();
      await updateLogsAndElapsed();
    }
    catch (err) {
      handleError("An error has occurred!", err);
    }
  };

  const handleBreakToggle = async (tasks?: string) => {
    if (!appUserId) return
    const now = new Date();
    try{
      if (!isOnBreak) {
        setBreakStartTime(now);
        setIsOnBreak(true);
        await postEntry(appUserId, "BreakOut", false, getSafeTasks(tasks));
      } else {
        setBreakStartTime(null);
        setIsOnBreak(false);
        await postEntry(appUserId, "BreakIn", false, getSafeTasks(tasks));
      }
      await updateLogsAndElapsed();
      updateIsWorking();
    }
    catch (err) {
      handleError("An error has occurred!", err);
    }
  };

  const handleLunchToggle = async (tasks?: string) => {
    if (!appUserId) return;
    const now = new Date();
    try{
      if (!isOnLunch) {
        setLunchStartTime(now);
        setIsOnLunch(true);
        await postEntry(appUserId, "LunchOut", false, getSafeTasks(tasks));
      } else {
        setLunchStartTime(null);
        setIsOnLunch(false);
        await postEntry(appUserId, "LunchIn", false, getSafeTasks(tasks));
      }
  
      await updateLogsAndElapsed();
      updateIsWorking();
    }
    catch (err) {
      handleError("An error has occurred!", err);
    } 
  };

  const dismissNotification = () => {
    setErrorNotification(null);
  };

  const requestEntry = async (type: string, time: string, entryId?: number,  comment?: string) => {
    if (!appUserId) {
      console.warn("❌ No appUserId found. Aborting requestEntry.");
      return;
    }
  
    try {
      const isoTime = new Date(time).toISOString();
  
      console.log("📤 Submitting entry request:", {
        userId: appUserId,
        type,
        time: isoTime,
        entryId: entryId
       
      });
  
      await postEntryRequest({
        userId: appUserId,
        entryId: entryId ?? undefined,
        type: type as "ClockIn" | "ClockOut" | "BreakIn" | "BreakOut" | "LunchIn" | "LunchOut",
        time: isoTime,
        
      });
  
      console.log("✅ Entry request submitted successfully");
    } catch (error: any) {
      console.error("❌ Failed to submit entry request:", error.message);
      handleError("Error submitting entry request", error);
    }
  };

  return {
    clockInTime,
    clockOutTime,
    breakStartTime,
    lunchStartTime,
    isOnBreak,
    isOnLunch,
    isWorking,
    elapsedTime,
    activeElapsed,
    breakElapsed,
    handleClockIn,
    handleClockOut,
    handleBreakToggle,
    handleLunchToggle,
    todaysLogs,
    activeTimeSeconds,
    breakTimeSeconds,
    weeklyTimeSeconds,
    weeklyDailyHours,
    errorNotification,
    dismissNotification,
    requestEntry,
  };
}
